import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, Phone, MapPin, Pencil, Printer, Search, Flower2, Plus, Trash2, Users2 } from 'lucide-react'
import {
  getCustomer, getCustomerInvoices, getCustomerEstimates, getCustomerBills, getCustomerAppointments,
  listRelatedCustomers, addRelatedCustomer, removeRelatedCustomer, listCustomers, type RelatedCustomerLink,
} from '../services/data'
import { PageHeader, Card, DataTable, Tabs, Spinner, Button, StatCard } from '../components/shared/ui'
import { customerLabel, currency, date } from '../lib/format'
import CustomerForm from '../components/CustomerForm'
import { printHtml } from '../lib/print'
import { toast } from 'react-toastify'
import type { Customer, Invoice, Estimate, Bill, Appointment } from '../types'

function apptTime(v: string | null): string {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d.getTime())) return String(v)
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [estimates, setEstimates] = useState<Estimate[]>([])
  const [bills, setBills] = useState<Bill[]>([])
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [related, setRelated] = useState<RelatedCustomerLink[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)

  const loadRelated = useCallback(() => {
    if (!id) return
    listRelatedCustomers(id).then(setRelated).catch(() => setRelated([]))
  }, [id])

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    // Load the customer first so we can pass its legacy IDs to the tab queries —
    // POS sales link by legacy_pos_id, IMS estimates/bills/appointments by
    // legacy_ims_id (a safety net for rows missing the new customer_id UUID).
    getCustomer(id)
      .then(async c => {
        setCustomer(c)
        const posId = (c?.legacy_pos_id as number | null) ?? null
        const imsId = (c?.legacy_ims_id as number | null) ?? null
        const [inv, est, bl, appt] = await Promise.all([
          getCustomerInvoices(id, posId), getCustomerEstimates(id, imsId),
          getCustomerBills(id, imsId), getCustomerAppointments(id, imsId),
        ])
        setInvoices(inv); setEstimates(est); setBills(bl); setAppointments(appt)
      })
      .finally(() => setLoading(false))
    loadRelated()
  }, [id, loadRelated])

  useEffect(() => { load() }, [load])

  if (loading) return <Spinner label="Loading customer…" />
  if (!customer) return <div className="text-gray-500">Customer not found.</div>

  const totalSpent = invoices.reduce((s, i) => s + (i.total ?? 0), 0)
  const hasSpringCleanup = bills.some(b => b.is_spring_cleanup)
  const hasConfidential = !!customer.confidential_notes

  const printDirections = () => printHtml(`Directions — ${customerLabel(customer)}`, `
    <h2>${customerLabel(customer)}</h2>
    <p>${[customer.address, customer.city, customer.state, customer.zip].filter(Boolean).join(', ')}</p>
    ${[customer.phone_home, customer.mobile, customer.phone_work].filter(Boolean).map(p => `<p>${p}</p>`).join('')}
    <hr/><h3>Directions</h3><pre style="font-family:inherit;white-space:pre-wrap">${customer.directions ?? 'No directions on file.'}</pre>
  `)

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate('/customers')} className="mb-4">
        <ArrowLeft size={16} /> Back to Customers
      </Button>
      <PageHeader
        title={customerLabel(customer)}
        subtitle={`Customer ID: ${customer.legacy_id ?? customer.legacy_ims_id ?? customer.id.slice(0, 8)}${customer.company_name ? ' · ' + customer.company_name : ''}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/customers')}><Search size={16} /> Customer Search</Button>
            <Button variant="secondary" onClick={printDirections}><Printer size={16} /> Print Directions</Button>
            <Button variant="secondary" onClick={() => setEditing(true)}><Pencil size={16} /> Edit</Button>
          </>
        }
      />

      {(hasConfidential || hasSpringCleanup) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {hasConfidential && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-sm font-medium border border-red-200">⚠ This customer has Confidential Notes!</span>}
          {hasSpringCleanup && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200"><Flower2 size={15} /> Spring Cleanup customer</span>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Lifetime Spend" value={currency(totalSpent)} accent="green" />
        <StatCard label="POS Sales" value={invoices.length} accent="blue" />
        <StatCard label="Estimates / Jobs" value={`${estimates.length} / ${bills.length}`} accent="purple" />
        <StatCard label="Store Credit" value={currency(customer.store_credit ?? 0)} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-1">
          <h3 className="font-semibold text-gray-800 mb-4">Contact</h3>
          <div className="space-y-2 text-sm">
            {customer.phone_home && <div className="flex items-center gap-2 text-gray-600"><Phone size={16} /> {customer.phone_home} <span className="text-xs text-gray-400">home</span></div>}
            {customer.phone_work && <div className="flex items-center gap-2 text-gray-600"><Phone size={16} /> {customer.phone_work} <span className="text-xs text-gray-400">work</span></div>}
            {customer.mobile && <div className="flex items-center gap-2 text-gray-600"><Phone size={16} /> {customer.mobile} <span className="text-xs text-gray-400">mobile</span></div>}
            {!customer.phone_home && !customer.phone_work && !customer.mobile && customer.phone && <div className="flex items-center gap-2 text-gray-600"><Phone size={16} /> {customer.phone}</div>}
            {customer.email && <div className="flex items-center gap-2 text-gray-600"><Mail size={16} /> {customer.email}</div>}
            {customer.email2 && <div className="flex items-center gap-2 text-gray-600"><Mail size={16} /> {customer.email2}</div>}
            <div className="flex items-start gap-2 text-gray-600">
              <MapPin size={16} className="mt-0.5" />
              <span>{[customer.address, customer.city, customer.state, customer.zip].filter(Boolean).join(', ') || '—'}</span>
            </div>
          </div>
          {(customer.balance ?? 0) !== 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-sm">
              <span className="text-gray-500">Balance</span><span className="font-medium">{currency(customer.balance ?? 0)}</span>
            </div>
          )}
          {(customer.comments || customer.notes) && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Notes</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{customer.comments || customer.notes}</p>
            </div>
          )}
        </Card>

        <Card className="p-5 lg:col-span-2">
          <Tabs tabs={[
            {
              id: 'appointments', label: 'Appointments', badge: appointments.length,
              content: (
                <DataTable
                  rows={appointments}
                  onRowClick={a => navigate(`/appointments/${a.id}`)}
                  emptyMessage="No appointments"
                  rowClassName={a => a.outcome_id === 8 ? 'bg-red-50' : ''}
                  defaultSort={{ key: 'appt_date', dir: 'desc' }}
                  columns={[
                    { key: 'appt_date', header: 'Appt. Date', render: a => date(a.appointment_date), sortValue: a => a.appointment_date },
                    { key: 'appt_time', header: 'Appt. Time', render: a => apptTime(a.appointment_time) },
                    { key: 'made', header: 'Date Made', render: a => date(a.appointment_made_date), sortValue: a => a.appointment_made_date },
                    { key: 'designer', header: 'Designer', render: a => (a.designer_name as string) || 'No Designer' },
                  ]}
                />
              ),
            },
            {
              id: 'estimates', label: 'Estimates', badge: estimates.length,
              content: (
                <DataTable
                  rows={estimates}
                  onRowClick={e => navigate(`/estimates/${e.id}`)}
                  emptyMessage="No estimates"
                  defaultSort={{ key: 'est_num', dir: 'desc' }}
                  columns={[
                    { key: 'est_num', header: 'Estimate #', render: e => e.bill_code || e.legacy_id || '—', sortValue: e => e.legacy_id },
                    { key: 'est_date', header: 'Estimate Date', render: e => date(e.estimate_date), sortValue: e => e.estimate_date },
                    { key: 'designer', header: 'Designer', render: e => (e.designer_name as string) || '—' },
                    { key: 'total', header: 'Total', render: e => currency(e.estimate_total), className: 'text-right', sortValue: e => e.estimate_total },
                  ]}
                />
              ),
            },
            {
              id: 'jobinvoices', label: 'Job Invoices', badge: bills.length,
              content: (
                <DataTable
                  rows={bills}
                  onRowClick={b => navigate(`/bills/${b.id}`)}
                  emptyMessage="No job invoices"
                  defaultSort={{ key: 'inv_id', dir: 'desc' }}
                  columns={[
                    { key: 'inv_id', header: 'Invoice ID', render: b => b.bill_code || b.legacy_bill_id || '—', sortValue: b => b.legacy_bill_id },
                    { key: 'inv_date', header: 'Invoice Date', render: b => date(b.bill_date), sortValue: b => b.bill_date },
                    { key: 'designer', header: 'Designer', render: b => b.designer_name || '—' },
                    { key: 'foreman', header: 'Foreman', render: b => b.foreman_name || '—' },
                    { key: 'spring', header: 'Spring Cleanup', render: b => b.is_spring_cleanup ? 'Yes' : '—' },
                  ]}
                />
              ),
            },
            {
              id: 'possales', label: 'POS Sales', badge: invoices.length,
              content: (
                <DataTable
                  rows={invoices}
                  onRowClick={i => navigate(`/transactions/${i.id}`)}
                  emptyMessage="No POS sales"
                  defaultSort={{ key: 'inv_num', dir: 'desc' }}
                  columns={[
                    { key: 'inv_num', header: 'Invoice #', render: i => `#${i.legacy_invoice_number ?? '—'}`, sortValue: i => i.legacy_invoice_number },
                    { key: 'date', header: 'Date', render: i => date(i.invoice_date), sortValue: i => i.invoice_date },
                    { key: 'total', header: 'Total', render: i => currency(i.total), className: 'text-right', sortValue: i => i.total },
                    { key: 'pay', header: 'Payment', render: i => i.payment_method || '—' },
                  ]}
                />
              ),
            },
            {
              id: 'related', label: 'Related', badge: related.length,
              content: <RelatedCustomers customerId={customer.id} links={related} onChange={loadRelated} />,
            },
            {
              id: 'directions', label: 'Directions',
              content: <p className="text-sm text-gray-700 whitespace-pre-wrap min-h-[6rem]">{customer.directions || 'No directions on file.'}</p>,
            },
            {
              id: 'confidential', label: 'Confidential Notes',
              content: <p className="text-sm text-gray-700 whitespace-pre-wrap min-h-[6rem]">{customer.confidential_notes || 'No confidential notes.'}</p>,
            },
          ]} />
        </Card>
      </div>

      {editing && (
        <CustomerForm
          mode="edit"
          customer={customer}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load() }}
        />
      )}
    </div>
  )
}

// ---- Related customers (port of POS frmCustomerRelationship) ----------------
function RelatedCustomers({ customerId, links, onChange }: {
  customerId: string; links: RelatedCustomerLink[]; onChange: () => void
}) {
  const navigate = useNavigate()
  const [adding, setAdding] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Customer[]>([])
  const [picked, setPicked] = useState<Customer | null>(null)
  const [desc, setDesc] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!q.trim() || picked) { setResults([]); return }
    let active = true
    listCustomers({ search: q, pageSize: 8 })
      .then(r => { if (active) setResults(r.rows.filter(c => c.id !== customerId)) })
      .catch(() => { if (active) setResults([]) })
    return () => { active = false }
  }, [q, picked, customerId])

  const save = async () => {
    if (!picked) { toast.error('Pick a customer to relate'); return }
    setSaving(true)
    try {
      await addRelatedCustomer(customerId, picked.id, desc || null)
      toast.success('Relationship added')
      setAdding(false); setPicked(null); setDesc(''); setQ(''); setResults([])
      onChange()
    } catch { toast.error('Failed to add relationship (managers only)') } finally { setSaving(false) }
  }

  const remove = async (rid: string) => {
    if (!window.confirm('Remove this relationship?')) return
    try { await removeRelatedCustomer(rid); toast.success('Relationship removed'); onChange() }
    catch { toast.error('Failed to remove relationship') }
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        {!adding && <Button variant="secondary" onClick={() => setAdding(true)}><Plus size={15} /> Add Relationship</Button>}
      </div>

      {adding && (
        <div className="border border-gray-200 rounded-lg p-3 space-y-2">
          {picked ? (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm">
              <span><b>{customerLabel(picked)}</b>{picked.company_name ? ` · ${picked.company_name}` : ''}</span>
              <button onClick={() => setPicked(null)} className="text-green-700 hover:underline text-xs">change</button>
            </div>
          ) : (
            <div className="relative">
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search a customer to relate…"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              {results.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {results.map(c => (
                    <button key={c.id} onClick={() => { setPicked(c); setResults([]) }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50">
                      <span className="font-medium">{customerLabel(c)}</span>
                      {c.company_name && <span className="text-gray-500"> · {c.company_name}</span>}
                      {(c.city || c.phone_home) && <span className="text-xs text-gray-400 block">{[c.city, c.phone_home].filter(Boolean).join(' · ')}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (e.g. Spouse, Business partner)"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setAdding(false); setPicked(null); setQ(''); setDesc('') }}>Cancel</Button>
            <Button onClick={save} disabled={saving || !picked}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      )}

      {links.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center flex flex-col items-center gap-2">
          <Users2 size={24} className="text-gray-300" /> No related customers.
        </p>
      ) : (
        <div className="divide-y divide-gray-100">
          {links.map(l => (
            <div key={l.relationship_id} className="flex items-center justify-between py-2">
              <button onClick={() => navigate(`/customers/${l.customer.id}`)} className="text-left">
                <span className="font-medium text-green-700 hover:underline">{customerLabel(l.customer)}</span>
                {l.customer.company_name && <span className="text-gray-500 text-sm"> · {l.customer.company_name}</span>}
                {l.description && <span className="text-xs text-gray-400 block">{l.description}</span>}
              </button>
              <button onClick={() => remove(l.relationship_id)} className="text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
